/**
 * Under the Hood Page - Portfolio Data Model Visualization
 * 
 * Interactive D3.js visualization showing the portfolio's data architecture
 * and entity relationships using force-directed graph layout.
 */

import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { getEntityCounts } from "@/lib/dataModelTransform";
import DataModelVisualization from "@/components/DataModelVisualization";
import { SkillCalculationExplanation } from "@/components/skills/modals/SkillCalculationExplanation";
import { Building2, Activity, FolderKanban, Award, Brain } from "lucide-react";

export default function UnderTheHood() {
  // Try to use real data, fall back to mock data if there are issues
  let entityCounts;
  
  try {
    entityCounts = getEntityCounts();
  } catch (error) {
    console.error('Error loading real data, using fallback:', error);
    // Fallback mock data
    entityCounts = { 
      companies: 5, 
      activities: 12, 
      projects: 8, 
      certificates: 3,
      skills: 45
    };
  }

  return (
    <main className="container mx-auto p-6 min-h-screen">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* Header Section */}
        <div className="text-center space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative inline-block"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-primary/10 to-transparent rounded-lg blur-lg"></div>
            <h1 className="text-4xl md:text-5xl font-bold relative z-10 px-6 py-3">
              Under the Hood
            </h1>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            Explore the data architecture powering this portfolio.
          </motion.p>
        </div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4"
        >
          <Card className="text-center">
            <CardContent className="pt-6">
              <Building2 className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <div className="text-2xl font-bold">{entityCounts.companies}</div>
              <div className="text-sm text-muted-foreground">Companies</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <Activity className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <div className="text-2xl font-bold">{entityCounts.activities}</div>
              <div className="text-sm text-muted-foreground">Activities</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <FolderKanban className="h-8 w-8 mx-auto mb-2 text-amber-500" />
              <div className="text-2xl font-bold">{entityCounts.projects}</div>
              <div className="text-sm text-muted-foreground">Projects</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <Award className="h-8 w-8 mx-auto mb-2 text-purple-500" />
              <div className="text-2xl font-bold">{entityCounts.certificates}</div>
              <div className="text-sm text-muted-foreground">Certificates</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <Brain className="h-8 w-8 mx-auto mb-2 text-red-500" />
              <div className="text-2xl font-bold">{entityCounts.skills}</div>
              <div className="text-sm text-muted-foreground">Skills</div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Data Model Visualization */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <DataModelVisualization />
        </motion.div>

        {/* Skill Calculation Engine Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <SkillCalculationExplanation />
        </motion.div>
      </motion.div>
    </main>
  );
}
